import { Link, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect } from "react";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import PartnersCarousel from "@/components/PartnersCarousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  MapPin,
  Handshake,
  ArrowRight,
  Sparkles,
  Building2,
  Clock3,
  ChevronRight,
  Layers,
  Users,
  Tag,
  ShoppingBag,
} from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { getPropertyImageUrl, getApiEndpoint, getBannerImageUrl, getDevelopmentLogoUrl } from "@/config/api";
import { getUrgentCardImageFilename, getUrgentCardImageUrl } from "@/lib/urgentPropertyImage";
import { getCoverImageUrl } from "@/lib/imageMap";
import { SearchService, type SearchResult } from "@/services/searchService";
import { developmentHref } from "@/lib/developmentLinks";
import { cn } from "@/lib/utils";
import { HomeSectionHeading } from "@/components/home/HomeSectionHeading";
import TopHeroTemplate, { type HeroSlide } from "@/components/layout/TopHeroTemplate";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import {
  DevelopmentService,
  parseDevelopmentPhotos,
  type Development,
} from "@/services/developmentService";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import PartnerPopup from "@/components/PartnerPopup";
import { BannerService, Banner } from "@/services/bannerService";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";
import apartment1 from "@/assets/apartment-1.jpg";
import resort1 from "@/assets/resort-1.jpg";
import penthouse1 from "@/assets/penthouse-1.jpg";
import natal1 from "@/assets/natal-1.jpg";
import natal2 from "@/assets/natal-2.jpg";
import natal3 from "@/assets/natal-3.jpg";

interface UrgentAvailability {
  id: number;
  property_id: number;
  start_date: string;
  end_date: string;
  price_per_night: number;
  max_guests: number;
  property_title: string;
  city: string;
  state: string;
  property_image: string | null;
  development_name?: string | null;
}

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();
  const [urgentAvailabilities, setUrgentAvailabilities] = useState<UrgentAvailability[]>([]);
  const [loadingUrgent, setLoadingUrgent] = useState(false);
  const [principalBanners, setPrincipalBanners] = useState<Banner[]>([]);
  const [destaqueBanners, setDestaqueBanners] = useState<Banner[]>([]);
  const [secundarioBanners, setSecundarioBanners] = useState<Banner[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loadingDevelopments, setLoadingDevelopments] = useState(true);
  const [saleProperties, setSaleProperties] = useState<SearchResult[]>([]);
  const [loadingSale, setLoadingSale] = useState(false);

  // Slides do carrossel principal — todos os banners "principal" cadastrados
  const principalSlides = useMemo<HeroSlide[]>(() => {
    if (principalBanners.length === 0) {
      return [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5].map(
        (image, index) => ({ id: `fallback-${index}`, image }),
      );
    }
    return principalBanners.map((banner) => ({
      id: banner.id,
      image: getBannerImageUrl(banner.image_path),
      backgroundColor: banner.background_color ?? undefined,
      title: banner.title ?? undefined,
      subtitle: banner.subtitle ?? undefined,
      text: banner.text ?? undefined,
      onView: () => BannerService.registerView(banner.id).catch(console.error),
      onClick: banner.link
        ? () => {
            BannerService.registerClick(banner.id).catch(console.error);
            window.location.href = banner.link!;
          }
        : () => BannerService.registerClick(banner.id).catch(console.error),
    }));
  }, [principalBanners]);

  const highlightBanner = useMemo(() => {
    if (destaqueBanners.length === 0) return null;
    return destaqueBanners[Math.floor(Math.random() * destaqueBanners.length)];
  }, [destaqueBanners]);

  const partnerBanner = useMemo(() => {
    if (secundarioBanners.length === 0) return null;
    return secundarioBanners[Math.floor(Math.random() * secundarioBanners.length)];
  }, [secundarioBanners]);

  // Buscar banners
  useEffect(() => {
    const loadBanners = async () => {
      try {
        const [principal, destaque, secundario] = await Promise.all([
          BannerService.getBannersByType('principal'),
          BannerService.getBannersByType('destaque'),
          BannerService.getBannersByType('secundario'),
        ]);
        setPrincipalBanners(principal);
        setDestaqueBanners(destaque);
        setSecundarioBanners(secundario);
      } catch (error) {
        console.error('Erro ao carregar banners:', error);
      }
    };
    loadBanners();
  }, []);

  // O banner principal agora é um carrossel — visualizações são registradas
  // por slide diretamente em `TopHeroTemplate` via callback `onView`.

  useEffect(() => {
    if (highlightBanner?.id) {
      BannerService.registerView(highlightBanner.id).catch(console.error);
    }
  }, [highlightBanner?.id]);

  useEffect(() => {
    if (partnerBanner?.id) {
      BannerService.registerView(partnerBanner.id).catch(console.error);
    }
  }, [partnerBanner?.id]);

  useEffect(() => {
    const loadDevelopments = async () => {
      try {
        setLoadingDevelopments(true);
        const list = await DevelopmentService.getAllDevelopments();
        setDevelopments(list);
      } catch (error) {
        console.error("Erro ao carregar resorts na home:", error);
        setDevelopments([]);
      } finally {
        setLoadingDevelopments(false);
      }
    };
    loadDevelopments();
  }, []);

  // Buscar períodos urgentes (menos de 30 dias para vencer)
  useEffect(() => {
    const loadUrgentAvailabilities = async () => {
      try {
        setLoadingUrgent(true);
        const response = await fetch(getApiEndpoint('/api/availability/urgent'));
        if (!response.ok) {
          throw new Error('Erro ao buscar disponibilidades urgentes');
        }
        const data = await response.json();
        // Limitar a 8 anúncios para exibição
        const shuffled = [...data].sort(() => Math.random() - 0.5).slice(0, 8);
        setUrgentAvailabilities(shuffled);
      } catch (error) {
        console.error('❌ Erro ao carregar disponibilidades urgentes:', error);
      } finally {
        setLoadingUrgent(false);
      }
    };

    loadUrgentAvailabilities();
  }, []);

  useEffect(() => {
    const loadSaleProperties = async () => {
      try {
        setLoadingSale(true);
        const results = await SearchService.searchProperties({ forSale: true });
        const shuffled = [...results].sort(() => Math.random() - 0.5).slice(0, 4);
        setSaleProperties(shuffled);
      } catch (error) {
        console.error("Erro ao carregar cotas à venda:", error);
        setSaleProperties([]);
      } finally {
        setLoadingSale(false);
      }
    };

    loadSaleProperties();
  }, []);

  const monthFilters = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const monthImages = [
      natal1,
      heroMuroAlto2,
      heroMuroAlto3,
      heroMuroAlto4,
      resort1,
      heroMuroAlto5,
      penthouse1,
      apartment1,
      heroMuroAlto1,
      heroMuroAlto2,
      natal2,
      natal3,
    ];
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const year = month < now.getMonth() + 1 ? currentYear + 1 : currentYear;
      const monthName = new Date(year, index, 1).toLocaleDateString("pt-BR", {
        month: "long",
      });
      const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const endDate = format(new Date(year, month, 0), "yyyy-MM-dd");
      return {
        id: month,
        label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        startDate,
        endDate,
        image: monthImages[index],
      };
    });
  }, []);

  const developmentCoverFallbacks = [
    heroMuroAlto1,
    heroMuroAlto2,
    heroMuroAlto3,
    heroMuroAlto4,
    heroMuroAlto5,
    resort1,
    apartment1,
    penthouse1,
    natal1,
    natal2,
    natal3,
  ];

  const getDevelopmentCoverUrl = (development: Development, index: number): string => {
    if (development.cover_image && development.cover_image.trim() !== "") {
      if (development.cover_image.startsWith("http://") || development.cover_image.startsWith("https://")) {
        return development.cover_image;
      }
      if (development.cover_image.startsWith("/")) {
        return development.cover_image;
      }
      return getDevelopmentLogoUrl(development.cover_image);
    }

    const parsedPhotos = parseDevelopmentPhotos(development.photos);

    const firstPhoto = parsedPhotos[0];
    if (firstPhoto) {
      if (firstPhoto.startsWith("http://") || firstPhoto.startsWith("https://")) return firstPhoto;
      return getDevelopmentLogoUrl(firstPhoto);
    }

    return developmentCoverFallbacks[index % developmentCoverFallbacks.length];
  };

  const goToSearchWithMonth = (monthId: number, startDate: string, endDate: string) => {
    setSelectedMonth(monthId);
    const params = new URLSearchParams();
    params.set("inicio", startDate);
    params.set("fim", endDate);
    navigate(`/busca?${params.toString()}`);
  };

  const parceiros = [
    {
      id: 1,
      nome: "Múltiplos Destinos",
      imagem: heroMuroAlto2,
      icone: MapPin,
      descricao: "Acesso a diversos resorts e destinos exclusivos em todo o Brasil",
    },
    {
      id: 2,
      nome: "Flexibilidade Total",
      imagem: heroMuroAlto1,
      icone: Layers,
      descricao: "Troque suas semanas e desfrute de diferentes experiências a cada viagem",
    },
    {
      id: 3,
      nome: "Experiências Únicas",
      imagem: heroMuroAlto3,
      icone: Sparkles,
      descricao: "Viva momentos inesquecíveis nos melhores resorts",
    },
    {
      id: 4,
      nome: "Facilidade de Reserva",
      imagem: heroMuroAlto5,
      icone: Calendar,
      descricao: "Sistema simples e intuitivo para reservar suas semanas de férias",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section — carrossel com todos os banners "principal" */}
      <TopHeroTemplate
        slides={principalSlides}
        navbarProps={{
          isAuthenticated,
          isOwner: userType === "owner",
          userName,
          userPhoto,
          userId,
          userType,
          cleanHeader: true,
          rightActionsCard: true,
        }}
        renderSlideContent={(slide) => {
          const hasContent = Boolean(slide.title || slide.subtitle || slide.text);
          if (!hasContent && principalBanners.length > 0) {
            return null;
          }
          return (
            <div className="relative pointer-events-none">
              {slide.title ? (
                <h1 className="text-2xl sm:text-3xl md:text-6xl font-bold text-white mb-3 drop-shadow-md leading-tight">
                  {slide.title}
                </h1>
              ) : (
                principalBanners.length === 0 && (
                  <h1 className="text-2xl sm:text-3xl md:text-6xl font-bold text-white mb-3 drop-shadow-md leading-tight">
                    Viva sua melhor experiência
                  </h1>
                )
              )}
              {slide.subtitle ? (
                <p className="text-sm sm:text-base md:text-2xl text-white/95 mb-3 drop-shadow-md leading-relaxed">
                  {slide.subtitle}
                </p>
              ) : (
                principalBanners.length === 0 && (
                  <p className="text-sm sm:text-base md:text-2xl text-white/95 mb-6 drop-shadow-md leading-relaxed">
                    Sua liberdade para explorar, trocar e desfrutar de semanas exclusivas nos mais desejados resorts.
                  </p>
                )
              )}
              {slide.text && !slide.title && (
                <p className="text-sm sm:text-base md:text-2xl text-white/95 mb-6 drop-shadow-md leading-relaxed">
                  {slide.text}
                </p>
              )}
            </div>
          );
        }}
      >
        <div className="mx-auto mt-6 sm:mt-8 md:mt-6 w-full max-w-none">
          <SearchBar compactHeight />
        </div>
      </TopHeroTemplate>

      {highlightBanner && (
        <section className="container mx-auto px-4 pt-5">
          <div
            className="group relative overflow-hidden rounded-2xl cursor-pointer"
            onClick={() => {
              if (highlightBanner.id) {
                BannerService.registerClick(highlightBanner.id).catch(console.error);
              }
              if (highlightBanner.link) {
                if (
                  highlightBanner.link.startsWith("http://") ||
                  highlightBanner.link.startsWith("https://")
                ) {
                  window.location.href = highlightBanner.link;
                  return;
                }
                navigate(highlightBanner.link);
                return;
              }
              navigate("/busca");
            }}
          >
            <img
              src={getBannerImageUrl(highlightBanner.image_path)}
              alt={highlightBanner.name}
              className="h-20 md:h-28 w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.01]"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />
            {(highlightBanner.title || highlightBanner.text) && (
              <div className="absolute inset-y-0 left-0 flex items-center px-4 py-3 md:px-5 text-white">
                <div>
                  <h2 className="text-base md:text-xl font-semibold leading-tight">
                    {highlightBanner.title || highlightBanner.text}
                  </h2>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="container mx-auto px-4 py-8 md:py-9" aria-labelledby="home-index-months-heading">
        <div className="mb-8 max-w-2xl">
          <HomeSectionHeading
            icon={Calendar}
            eyebrow="Busca por mês"
            title="Escolha quando viajar"
            description="Toque no mês para abrir a busca com check-in e check-out já sugeridos."
            titleId="home-index-months-heading"
          />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
          {monthFilters.map((month) => (
            <button
              key={month.id}
              type="button"
              className={`group relative overflow-hidden rounded-xl text-left border transition-[box-shadow,transform] duration-150 ease-out ${
                selectedMonth === month.id
                  ? "ring-2 ring-primary border-primary"
                  : "hover:shadow-sm hover:-translate-y-px"
              }`}
              onClick={() => goToSearchWithMonth(month.id, month.startDate, month.endDate)}
            >
              <img
                src={month.image}
                alt={month.label}
                className="h-24 w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.01]"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-3 text-white">
                <p className="font-semibold leading-none">{month.label}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section
        className="relative overflow-hidden border-y bg-gradient-to-b from-slate-50/80 via-background to-muted/15 py-12 md:py-14 dark:from-muted/20 dark:via-background dark:to-background"
        aria-labelledby="home-index-urgent-heading"
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18] dark:opacity-[0.1]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--muted-foreground) / 0.12) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="container relative mx-auto px-4">
          <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <HomeSectionHeading
              className="max-w-2xl min-w-0 flex-1"
              icon={Sparkles}
              eyebrow="Explore as estadias"
              title="Imóveis com estadia a expirar"
              description="Destaques das próximas semanas: check-in perto e últimas janelas — ofertas em tempo limitado, ao estilo das melhores vitrines."
              descriptionClassName="line-clamp-1"
              titleId="home-index-urgent-heading"
            />
            {!loadingUrgent && urgentAvailabilities.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className="h-11 shrink-0 gap-2 rounded-full border-primary/20 px-6 shadow-sm transition-colors duration-150 hover:bg-primary hover:text-primary-foreground"
                onClick={() => navigate("/busca")}
              >
                Ver todas na busca
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </div>

          {loadingUrgent ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                  <div className="aspect-[16/10] animate-pulse bg-muted" />
                  <div className="space-y-3 p-6">
                    <div className="h-6 w-4/5 animate-pulse rounded-md bg-muted" />
                    <div className="h-4 w-3/5 animate-pulse rounded-md bg-muted" />
                    <div className="h-12 w-full animate-pulse rounded-md bg-muted mt-4" />
                  </div>
                </div>
              ))}
            </div>
          ) : urgentAvailabilities.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-card/50 px-6 py-14 text-center">
              <Clock3 className="mx-auto h-10 w-10 text-muted-foreground/60" aria-hidden />
              <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Nenhum imóvel com período nos próximos dias. Ajuste as datas na busca para encontrar estadias
                disponíveis.
              </p>
              <Button className="mt-6 rounded-full" variant="secondary" onClick={() => navigate("/busca")}>
                Ir para a busca
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {urgentAvailabilities.map((availability) => {
                const startDate = new Date(availability.start_date);
                const endDate = new Date(availability.end_date);
                const nights = Math.ceil(
                  (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
                );
                const totalPrice = availability.price_per_night * nights;
                const imgSrc = getUrgentCardImageUrl(availability);
                const fullFilename = getUrgentCardImageFilename(availability);

                return (
                  <Link
                    key={availability.id}
                    to={`/imovel/${availability.property_id}`}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm transition-[box-shadow,transform] duration-150 ease-out hover:-translate-y-px hover:shadow-md dark:border-border/60"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={imgSrc}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.01]"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (el.dataset.fallback === "1" || !fullFilename) return;
                          el.dataset.fallback = "1";
                          el.src = getPropertyImageUrl(availability.property_id, fullFilename);
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <p className="line-clamp-2 text-lg font-bold leading-snug drop-shadow-md md:text-xl">
                          {availability.property_title}
                        </p>
                        {availability.development_name ? (
                          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-white/90">
                            <Building2 className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            <span className="line-clamp-1">{availability.development_name}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-5 md:p-6">
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <span>
                          {availability.city}, {availability.state}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-border/60 pt-4 text-xs font-medium text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1">
                          <Calendar className="h-3.5 w-3.5 text-primary" aria-hidden />
                          {format(startDate, "dd MMM", { locale: ptBR })} —{" "}
                          {format(endDate, "dd MMM yyyy", { locale: ptBR })}
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1">
                          <Users className="h-3.5 w-3.5 text-primary" aria-hidden />
                          Até {availability.max_guests} hóspedes
                        </span>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1">
                          {nights} noites
                        </span>
                      </div>

                      <div className="mt-5 flex flex-wrap items-end justify-between gap-3 border-t border-border/50 pt-4">
                        <div>
                          <p className="text-2xl font-bold tabular-nums text-primary md:text-[1.65rem]">
                            R${" "}
                            {totalPrice.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            R${" "}
                            {availability.price_per_night.toLocaleString("pt-BR", {
                              minimumFractionDigits: 2,
                            })}{" "}
                            / noite · {nights} noites
                          </p>
                        </div>
                        <span className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors duration-150 group-hover:bg-primary/90 md:h-11 md:px-6">
                          Ver imóvel
                          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="border-y bg-muted/15 py-10 md:py-11" aria-labelledby="home-index-partners-heading">
        <div className="container mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <HomeSectionHeading
              icon={Handshake}
              eyebrow="Rede de parceiros"
              title="Parceiros e benefícios"
              description="Parceiros e resorts com vouchers e ofertas exclusivas quando disponíveis."
              titleId="home-index-partners-heading"
            />
          </div>
          <PartnersCarousel />
        </div>
      </section>

      {partnerBanner && (
        <section className="container mx-auto px-4 py-6 md:py-7">
          <div
            className="group relative overflow-hidden rounded-2xl cursor-pointer border bg-gradient-to-r from-orange-500 to-amber-500"
            onClick={() => {
              if (partnerBanner.id) {
                BannerService.registerClick(partnerBanner.id).catch(console.error);
              }
              if (partnerBanner.link) {
                if (partnerBanner.link.startsWith("http://") || partnerBanner.link.startsWith("https://")) {
                  window.location.href = partnerBanner.link;
                  return;
                }
                navigate(partnerBanner.link);
                return;
              }
              navigate("/lp/paradise");
            }}
          >
            <img
              src={getBannerImageUrl(partnerBanner.image_path)}
              alt={partnerBanner.name}
              className="h-28 md:h-36 w-full object-cover opacity-35 group-hover:opacity-42 transition-opacity duration-150"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/35 to-transparent" />
            <div className="absolute inset-0 flex items-center justify-between px-5 md:px-8">
              {(partnerBanner.title || partnerBanner.subtitle) && (
                <div className="text-white">
                  {partnerBanner.title && (
                    <h3 className="text-lg md:text-2xl font-semibold">{partnerBanner.title}</h3>
                  )}
                  {partnerBanner.subtitle && (
                    <p className="text-sm text-white/90 mt-1">{partnerBanner.subtitle}</p>
                  )}
                </div>
              )}
              <div className="hidden md:flex items-center justify-center rounded-full bg-white/20 p-3 text-white backdrop-blur ml-auto">
                <Handshake className="h-5 w-5" aria-hidden />
                <span className="sr-only">Parceiros</span>
              </div>
            </div>
          </div>
        </section>
      )}

      <section
        className="relative overflow-hidden border-y bg-gradient-to-b from-orange-50/40 via-background to-muted/15 py-12 md:py-14 dark:from-orange-950/10 dark:via-background dark:to-background"
        aria-labelledby="home-index-sale-heading"
      >
        <div className="container relative mx-auto px-4">
          <div className="mb-8 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <HomeSectionHeading
              className="max-w-2xl min-w-0 flex-1"
              icon={ShoppingBag}
              eyebrow="Compre sua cota"
              title="Cotas à venda"
              description="Imóveis disponíveis para compra de cota em diferentes resorts — oportunidades selecionadas aleatoriamente para você."
              titleId="home-index-sale-heading"
            />
            {!loadingSale && saleProperties.length > 0 && (
              <Button
                type="button"
                variant="outline"
                className="h-11 shrink-0 gap-2 rounded-full border-orange-300/60 px-6 shadow-sm transition-colors duration-150 hover:bg-orange-500 hover:text-white"
                onClick={() => navigate("/venda")}
              >
                Ver todas à venda
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            )}
          </div>

          {loadingSale ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                  <div className="aspect-[16/10] animate-pulse bg-muted" />
                  <div className="space-y-3 p-6">
                    <div className="h-6 w-4/5 animate-pulse rounded-md bg-muted" />
                    <div className="h-4 w-3/5 animate-pulse rounded-md bg-muted" />
                    <div className="h-12 w-full animate-pulse rounded-md bg-muted mt-4" />
                  </div>
                </div>
              ))}
            </div>
          ) : saleProperties.length === 0 ? (
            <div className="rounded-2xl border border-dashed bg-card/50 px-6 py-14 text-center">
              <Tag className="mx-auto h-10 w-10 text-muted-foreground/60" aria-hidden />
              <p className="mt-4 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                Nenhuma cota à venda no momento. Volte em breve ou explore a busca de venda.
              </p>
              <Button
                className="mt-6 rounded-full bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => navigate("/venda")}
              >
                Buscar cotas
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {saleProperties.map((result) => {
                const property = result.property;
                const coverSrc = getCoverImageUrl(property, true);

                return (
                  <Link
                    key={property.id}
                    to={`/imovel-venda/${property.id}`}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm transition-[box-shadow,transform] duration-150 ease-out hover:-translate-y-px hover:shadow-md dark:border-border/60"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={coverSrc}
                        alt=""
                        className="h-full w-full object-cover transition-transform duration-150 ease-out group-hover:scale-[1.01]"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.dataset.fallback === "1") return;
                          img.dataset.fallback = "1";
                          img.src = getCoverImageUrl(property, false);
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute top-3 right-3 rounded-full bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white shadow-md">
                        À venda
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <p className="line-clamp-2 text-lg font-bold leading-snug drop-shadow-md md:text-xl">
                          {property.title}
                        </p>
                        {property.development_name ? (
                          <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-white/90">
                            <Building2 className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            <span className="line-clamp-1">{property.development_name}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-5 md:p-6">
                      <div className="flex items-start gap-2 text-sm text-muted-foreground">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <span className="line-clamp-2">
                          {property.city}
                          {property.state ? `, ${property.state}` : ""}
                        </span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1 border-t border-border/60 pt-4 text-xs font-medium text-muted-foreground">
                        {property.bedrooms != null && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1">
                            {property.bedrooms} quartos
                          </span>
                        )}
                        {property.max_guests != null && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1">
                            <Users className="h-3.5 w-3.5 text-primary" aria-hidden />
                            Até {property.max_guests} hóspedes
                          </span>
                        )}
                      </div>

                      <div className="mt-5 flex justify-end border-t border-border/50 pt-4">
                        <span className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-5 text-sm font-semibold text-white transition-colors duration-150 group-hover:bg-orange-600 md:h-11">
                          Comprar
                          <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {(loadingDevelopments || developments.length > 0) && (
        <section
          id="resorts"
          className="relative overflow-x-hidden border-t bg-gradient-to-b from-background via-primary/[0.02] to-muted/20 pt-12 pb-10 md:pt-16 md:pb-11 scroll-mt-24"
          aria-labelledby="home-index-resorts-heading"
        >
          <div className="pointer-events-none absolute -left-32 top-24 h-48 w-48 rounded-full bg-primary/4 blur-xl" />
          <div className="pointer-events-none absolute -right-24 bottom-16 h-44 w-44 rounded-full bg-amber-400/8 blur-xl" />

          <div className="container relative mx-auto px-4">
            <div className="mb-8 max-w-2xl text-center md:mx-0 md:text-left">
              <HomeSectionHeading
                icon={Layers}
                eyebrow="Categorias de resort"
                title="Resorts"
                description="Arraste ou use as setas — cartões com leve inclinação, até cinco visíveis em telas largas."
                titleId="home-index-resorts-heading"
              />
            </div>

            {loadingDevelopments ? (
              <div className="flex justify-center gap-4 overflow-hidden py-8">
                {[1, 2, 3, 4, 5].map((s) => (
                  <div
                    key={s}
                    className="h-64 w-[70%] shrink-0 animate-pulse rounded-3xl bg-muted/90 sm:w-[45%] md:w-[30%] lg:w-[23%] xl:w-[18%]"
                  />
                ))}
              </div>
            ) : developments.length === 0 ? null : (
              <Carousel
                opts={{
                  align: "start",
                  loop: developments.length > 5,
                  dragFree: false,
                }}
                className="relative z-0 isolate w-full px-10 md:px-14"
              >
                <CarouselPrevious
                  type="button"
                  variant="outline"
                  className="absolute left-0 top-[38%] z-20 h-10 w-10 -translate-y-1/2 border-primary/25 bg-background/95 shadow-md backdrop-blur-sm hover:bg-background md:h-11 md:w-11"
                />
                <CarouselContent
                  viewportClassName="pt-4 pb-12 md:pb-16"
                  className="-ml-3 md:-ml-4 items-stretch"
                >
                  {developments.map((d, index) => {
                    const href = developmentHref(d);
                    const locationParts = [d.city, d.state].filter(Boolean);
                    const locationLine =
                      locationParts.length > 0 ? locationParts.join(" - ") : null;
                    const hasLogo = typeof d.logo === "string" && d.logo.trim() !== "";
                    const coverImage = getDevelopmentCoverUrl(d, index);
                    const normalizedName = d.name.trim().toLowerCase();
                    const hideCenterOverlayForCover =
                      normalizedName === "exclusive gramado" || normalizedName === "porto 2 life";
                    const tiltCycle = [
                      "-rotate-[1.5deg] translate-y-2",
                      "rotate-[1.2deg] translate-y-2",
                      "-rotate-1 -translate-y-0.5",
                      "rotate-[1.5deg] translate-y-1",
                      "-rotate-[0.6deg] translate-y-1",
                    ];
                    const tilt = tiltCycle[index % tiltCycle.length];

                    return (
                      <CarouselItem
                        key={d.id}
                        className="pl-3 md:pl-4 basis-[82%] sm:basis-[46%] md:basis-[32%] lg:basis-1/4 xl:basis-1/5 overflow-visible"
                      >
                        <Link
                          to={href}
                          className={cn(
                            "group relative z-10 mx-auto block max-w-[280px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:max-w-none",
                            "transition-transform duration-150 ease-out",
                            "hover:z-30 hover:scale-[1.01]",
                            tilt,
                            "hover:rotate-0 hover:translate-y-0",
                          )}
                        >
                          <div
                            className={cn(
                              "overflow-hidden rounded-3xl border-2 border-white/70 bg-card shadow-md shadow-black/[0.06] ring-1 ring-black/[0.04]",
                              "transition-shadow duration-150 group-hover:border-primary/20 group-hover:shadow-md dark:border-white/10 dark:ring-white/5",
                            )}
                          >
                            <div className="relative aspect-[4/3] bg-gradient-to-br from-muted/80 to-muted/40">
                              <img
                                src={coverImage}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="lazy"
                                decoding="async"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />
                              <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-primary/[0.18] to-transparent" />
                              {hasLogo && !hideCenterOverlayForCover ? (
                                <div className="absolute inset-0 flex items-center justify-center p-8">
                                  <img
                                    src={getDevelopmentLogoUrl(d.logo)}
                                    alt=""
                                    className="relative z-[1] max-h-[78%] max-w-[88%] rounded-2xl bg-white/80 p-3 object-contain shadow-md backdrop-blur-[1px] transition-transform duration-150 group-hover:scale-[1.01]"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              ) : (
                                <div className="absolute inset-x-0 bottom-0 z-[1] p-4 text-white">
                                  <p className="line-clamp-2 text-sm font-semibold leading-tight drop-shadow-sm">
                                    {d.name}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="space-y-1 px-5 pb-5 pt-2 text-center">
                              <h3 className="text-base font-bold leading-snug text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                                {d.name}
                              </h3>
                              {locationLine ? (
                                <p className="text-xs font-medium text-muted-foreground line-clamp-2">
                                  {locationLine}
                                </p>
                              ) : null}
                              <span className="mt-2 inline-flex items-center justify-center gap-1 text-xs font-semibold text-primary opacity-90 group-hover:opacity-100">
                                Explorar
                                <ChevronRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                              </span>
                            </div>
                          </div>
                        </Link>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
                <CarouselNext
                  type="button"
                  variant="outline"
                  className="absolute right-0 top-[38%] z-20 h-10 w-10 -translate-y-1/2 border-primary/25 bg-background/95 shadow-md backdrop-blur-sm hover:bg-background md:h-11 md:w-11"
                />
              </Carousel>
            )}
          </div>
        </section>
      )}

      {/* Diferenciais da Live Stay */}
      <section className="border-t bg-muted/25 py-10 md:py-11">
        <div className="container mx-auto px-4">
          <div className="mb-6 max-w-2xl text-left">
            <HomeSectionHeading
              icon={Sparkles}
              eyebrow="Benefícios Live Stay"
              title="Diferenciais da Live Stay"
              description="Descubra por que escolher a Live Stay para suas próximas férias e experiências únicas."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {parceiros.map((parceiro) => {
              const Icon = parceiro.icone;
              return (
                <Card
                  key={parceiro.id}
                  className="group cursor-pointer overflow-hidden border shadow-sm hover:shadow-md transition-[box-shadow,transform] duration-150 ease-out hover:-translate-y-px bg-card"
                >
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="w-24 h-24 mb-4 rounded-full overflow-hidden bg-background shadow-sm">
                      <img
                        src={parceiro.imagem}
                        alt={parceiro.nome}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-150 ease-out"
                      />
                    </div>
                    <div className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Icon className="h-4 w-4" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{parceiro.nome}</h3>
                    <p className="text-sm text-muted-foreground">{parceiro.descricao}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popup de Parceiro */}
      <PartnerPopup />

      <Footer />
    </div>
  );
};

export default Home;
